import { HoverIdleModel } from "@/features/hoverIdle/core/hoverIdleModel";
import {
  HovSkillModel,
  type HovSkillState,
} from "@/features/hoverIdle/hov/hovSkillModel";
import {
  TimSkillModel,
  type TimSkillState,
} from "@/features/hoverIdle/tim/timSkillModel";
import type {
  HoverIdleModelState,
  HoverIdleConfig,
} from "@/features/hoverIdle/types";
import type { AchievementsState } from "@/features/achievements/types";
import type { RecordsState } from "@/features/records/types";
import {
  createHoverIdleSession,
  type HoverIdleSession,
} from "@/features/hoverIdle/persistence/createHoverIdleSession";
import {
  createEmptyHovSkillState,
  createEmptyTimSkillState,
  emptyAchievementsState,
  emptyRecordsState,
} from "@/features/hoverIdle/persistence/hoverIdleSaveData";
import type { HoverIdleSaveStorage } from "@/features/hoverIdle/persistence/hoverIdleStorage";
import type { EntitlementSnapshot } from "@/features/payment/types";
import { computePersistentEffects } from "@/features/achievements/achievementEffects";
import type { HoverIdleSessionLifecycleEvent } from "@/features/hoverIdle/persistence/createHoverIdleSession";

export interface CreateHoverIdleAppSessionOptions {
  readonly storage: HoverIdleSaveStorage;
  readonly nowMs: () => number;
  readonly config?: HoverIdleConfig;
  readonly ownerEntitlementSnapshot?: EntitlementSnapshot;
  readonly startupAdminTimeScale?: number;
  readonly startupSystemTimeScale?: number;
  readonly initialModelState?: HoverIdleModelState | null;
  readonly initialHovSkillState?: HovSkillState | null;
  readonly initialTimSkillState?: TimSkillState | null;
  readonly initialAchievementsState?: AchievementsState | null;
  readonly initialRecordsState?: RecordsState | null;
  readonly patchInitialModelState?: (
    state: HoverIdleModelState,
  ) => HoverIdleModelState;
  readonly logLoadFailure?: (reason: string) => void;
  readonly logInitialStateFailure?: (reason: string) => void;
  readonly onLifecycleEvent?: (event: HoverIdleSessionLifecycleEvent) => void;
}

export async function createHoverIdleAppSession(
  options: CreateHoverIdleAppSessionOptions,
): Promise<HoverIdleSession> {
  if (
    options.initialModelState !== undefined &&
    options.initialModelState !== null
  ) {
    const model = new HoverIdleModel(options.config);
    const hovSkillModel = new HovSkillModel();
    const timSkillModel = new TimSkillModel();
    try {
      if (options.ownerEntitlementSnapshot !== undefined) {
        model.setOwnerEntitlementSnapshot(options.ownerEntitlementSnapshot);
      }
      const restoredAchievementsState =
        options.initialAchievementsState ?? emptyAchievementsState;
      model.setAchievementPersistentEffects(
        computePersistentEffects(restoredAchievementsState.unlockedIds),
      );
      const initialModelState =
        options.patchInitialModelState?.(options.initialModelState) ??
        options.initialModelState;
      model.restoreState(initialModelState);
      hovSkillModel.restoreState(
        options.initialHovSkillState ?? createEmptyHovSkillState(),
      );
      timSkillModel.restoreState(
        options.initialTimSkillState ?? createEmptyTimSkillState(),
      );
      return {
        model,
        hovSkillState: hovSkillModel.exportState(),
        timSkillState: timSkillModel.exportState(),
        achievementsState: restoredAchievementsState,
        recordsState: options.initialRecordsState ?? emptyRecordsState,
        timestamps: null,
        offlineReward: null,
        startupOfflineReward: null,
        loadFailureMessage: null,
      };
    } catch (error) {
      options.logInitialStateFailure?.(reasonFromUnknown(error));
    }
  }

  return createHoverIdleSession({
    storage: options.storage,
    nowMs: options.nowMs,
    config: options.config,
    ownerEntitlementSnapshot: options.ownerEntitlementSnapshot,
    startupAdminTimeScale: options.startupAdminTimeScale,
    startupSystemTimeScale: options.startupSystemTimeScale,
    patchInitialModelState: options.patchInitialModelState,
    logLoadFailure: options.logLoadFailure,
    onLifecycleEvent: options.onLifecycleEvent,
  });
}

function reasonFromUnknown(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
